import { useQuery } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import apiClient, { Evidence as EvidenceType, Case } from '../api/client';
import { HashDisplay, HashBadge } from '../components/ui/HashDisplay';
import { EvidenceViewerModal } from '../components/ui/FilePreview';
import { useAuth } from '../contexts/AuthContext';
import {
  FileBox,
  Download,
  History,
  Search,
  Filter,
  Shield,
  ArrowLeft,
  Eye,
  CheckCircle,
  Loader2,
  AlertCircle,
  Lock,
  Sparkles,
} from 'lucide-react';
import { useState, useMemo } from 'react';
import { format } from 'date-fns';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
}

// Helper to check if user is assigned to a case
function isUserAssignedToCase(_userEmail: string, userName: string, caseData: Case): boolean {
  const userNameLower = userName.toLowerCase();
  return !!(  
    caseData.assignedTo?.toLowerCase().includes(userNameLower) ||
    caseData.assignedJudge?.toLowerCase().includes(userNameLower) ||
    caseData.prosecutingAttorney?.toLowerCase().includes(userNameLower) ||
    caseData.defenseAttorney?.toLowerCase().includes(userNameLower) ||
    caseData.leadInvestigator?.toLowerCase().includes(userNameLower)
  );
}

export default function Evidence() {
  const { evidenceId } = useParams<{ evidenceId: string }>();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [caseFilter, setCaseFilter] = useState<string>('all');

  // If we have an evidenceId, show single evidence detail
  const { data: singleEvidence, isLoading: singleLoading } = useQuery({
    queryKey: ['evidence', evidenceId],
    queryFn: () => apiClient.getEvidence(evidenceId!),
    enabled: !!evidenceId,
  });

  // Get all cases
  const { data: cases } = useQuery({
    queryKey: ['cases'],
    queryFn: () => apiClient.getCases(),
    enabled: !evidenceId,
  });

  // Filter cases based on user role
  const accessibleCases = useMemo(() => {
    if (!cases || !user) return [];
    
    // CaseManager and SystemAdmin can see all cases
    if (user.role === 'CaseManager' || user.role === 'SystemAdmin') {
      return cases;
    }
    
    // PublicSubmitter only sees cases they've submitted to (handled differently)
    if (user.role === 'PublicSubmitter') {
      return cases; // We'll filter evidence by uploadedBy instead
    }
    
    // LawEnforcement and Attorney only see assigned cases
    return cases.filter(c => isUserAssignedToCase(user.email, user.name, c));
  }, [cases, user]);

  // Fetch evidence for all accessible cases
  const { data: allEvidenceArrays, isLoading: listLoading } = useQuery({
    queryKey: ['all-evidence', accessibleCases.map(c => c.caseNumber).join(',')],
    queryFn: async () => {
      const evidencePromises = accessibleCases.map(c => 
        apiClient.getCaseEvidence(c.caseNumber).catch(() => [])
      );
      return Promise.all(evidencePromises);
    },
    enabled: !evidenceId && accessibleCases.length > 0,
  });

  // Flatten and filter evidence
  const allEvidence = useMemo(() => {
    if (!allEvidenceArrays) return [];
    const flattened = allEvidenceArrays.flat();
    
    // PublicSubmitter only sees their own uploads
    if (user?.role === 'PublicSubmitter') {
      return flattened.filter(e => 
        e.uploadedBy === user.email || 
        e.uploadedBy?.toLowerCase().includes(user.name.toLowerCase())
      );
    }
    
    return flattened;
  }, [allEvidenceArrays, user]);

  if (evidenceId) {
    // Single evidence view
    if (singleLoading) {
      return (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
        </div>
      );
    }

    if (!singleEvidence) {
      return (
        <div className="text-center py-12">
          <p className="text-gray-500">Evidence not found</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <Link
          to="/evidence"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to evidence
        </Link>

        <EvidenceDetailCard evidence={singleEvidence} />
      </div>
    );
  }

  // Evidence list view
  const filteredEvidence = allEvidence?.filter((item) => {
    const matchesSearch =
      item.fileName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || item.evidenceType === typeFilter;
    const matchesCase = caseFilter === 'all' || item.caseNumber === caseFilter;
    return matchesSearch && matchesType && matchesCase;
  });

  const evidenceTypes = [...new Set(allEvidence?.map((e) => e.evidenceType) ?? [])];
  const caseNumbers = [...new Set(allEvidence?.map((e) => e.caseNumber) ?? [])];

  // Role-based access info message
  const getAccessMessage = () => {
    if (!user) return null;
    switch (user.role) {
      case 'PublicSubmitter':
        return 'Showing evidence you have submitted.';
      case 'LawEnforcement':
      case 'Attorney':
        return `Showing evidence from ${accessibleCases.length} case(s) you are assigned to.`;
      case 'CaseManager':
      case 'SystemAdmin':
        return 'Showing all evidence across all cases.';
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{user?.role === 'PublicSubmitter' ? 'My Submissions' : 'Evidence'}</h1>
          <p className="text-gray-600 mt-1">
            {getAccessMessage()}
          </p>
        </div>
        <Link
          to="/evidence/upload"
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700"
        >
          Upload Evidence
        </Link>
      </div>

      {/* Role-based access notice for restricted users */}
      {user && (user.role === 'LawEnforcement' || user.role === 'Attorney') && (
        <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <Lock className="h-5 w-5 text-blue-600 flex-shrink-0" />
          <p className="text-sm text-blue-800">
            You have access to evidence from cases where you are assigned as{' '}
            {user.role === 'LawEnforcement' ? 'Lead Investigator' : 'Prosecuting or Defense Attorney'}.
          </p>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search evidence..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-5 w-5 text-gray-400" />
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">All Types</option>
            {evidenceTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
          <select
            value={caseFilter}
            onChange={(e) => setCaseFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">All Cases</option>
            {caseNumbers.map((caseNum) => (
              <option key={caseNum} value={caseNum}>
                {caseNum}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Evidence grid */}
      {listLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
        </div>
      ) : filteredEvidence?.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <FileBox className="h-12 w-12 text-gray-400 mx-auto" />
          <p className="text-gray-500 mt-4">No evidence found</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
          {filteredEvidence?.map((item) => (
            <EvidenceCard key={item.id} evidence={item} />
          ))}
        </div>
      )}
    </div>
  );
}

function EvidenceCard({ evidence }: { evidence: EvidenceType }) {
  const [showViewer, setShowViewer] = useState(false);
  const [showDownloadConfirm, setShowDownloadConfirm] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState<{
    verified: boolean;
    message: string;
  } | null>(null);

  const handleDownloadClick = () => setShowDownloadConfirm(true);

  const handleDownload = async () => {
    setShowDownloadConfirm(false);
    setIsDownloading(true);
    try {
      await apiClient.logDownloadEvent(evidence.id).catch(() => {});
      const { url, filename } = await apiClient.downloadEvidence(evidence.id);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      setDownloadSuccess(true);
      setTimeout(() => setDownloadSuccess(false), 3000);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleVerifyIntegrity = async () => {
    setIsVerifying(true);
    setVerificationResult(null);
    try {
      const result = await apiClient.verifyIntegrity(evidence.id);
      setVerificationResult({
        verified: result.verified,
        message: result.message,
      });
      // Clear result after 5 seconds
      setTimeout(() => setVerificationResult(null), 5000);
    } catch (err) {
      setVerificationResult({
        verified: false,
        message: 'Verification failed - please try again',
      });
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex flex-col min-w-0">
        <div className="flex items-start justify-between min-w-0">
          <div className="flex items-center gap-3 min-w-0">
            <FileBox className="h-8 w-8 text-gray-400 flex-shrink-0" />
            <div className="min-w-0">
              <p className="font-medium text-gray-900 truncate">
                {evidence.fileName}
              </p>
              <p className="text-sm text-gray-500">{evidence.evidenceType}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {evidence.AiIndexedAt && (
              <span title={`AI Indexed: ${new Date(evidence.AiIndexedAt).toLocaleString()}`}>
                <Sparkles className="h-5 w-5 text-violet-500" />
              </span>
            )}
            {evidence.legalHold && (
              <span title="Legal Hold">
                <Shield className="h-5 w-5 text-amber-500" />
              </span>
            )}
          </div>
        </div>

        <p className="mt-3 text-sm text-gray-600 line-clamp-2">{evidence.description}</p>

        {/* Verification result banner */}
        {verificationResult && (
          <div className={`mt-3 p-2 rounded-lg flex items-center gap-2 text-sm ${
            verificationResult.verified 
              ? 'bg-green-50 text-green-800' 
              : 'bg-red-50 text-red-800'
          }`}>
            {verificationResult.verified ? (
              <CheckCircle className="h-4 w-4" />
            ) : (
              <AlertCircle className="h-4 w-4" />
            )}
            <span>{verificationResult.message}</span>
          </div>
        )}

        <div className="mt-4 flex items-center justify-between text-xs">
          <span className="text-gray-500">{formatFileSize(evidence.fileSize)}</span>
          <div className="flex items-center gap-2">
            <HashBadge hash={evidence.sha256Hash} verified={evidence.integrityStatus === 'verified'} />
            <button
              onClick={handleVerifyIntegrity}
              disabled={isVerifying}
              className="p-1 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
              title="Verify Integrity"
            >
              {isVerifying ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Shield className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-200 flex items-center gap-2">
          <button
            onClick={() => setShowViewer(true)}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg"
          >
            <Eye className="h-4 w-4" />
            View
          </button>
          <Link
            to={`/custody/${evidence.id}`}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg"
          >
            <History className="h-4 w-4" />
            Custody
          </Link>
          <button
            onClick={handleDownloadClick}
            disabled={isDownloading}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm text-primary-600 hover:bg-primary-50 rounded-lg disabled:opacity-50"
          >
            {isDownloading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : downloadSuccess ? (
              <CheckCircle className="h-4 w-4 text-green-600" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            {downloadSuccess ? 'Downloaded' : 'Download'}
          </button>
        </div>
      </div>

      {/* Download Confirmation Modal */}
      {showDownloadConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowDownloadConfirm(false)} />
          <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Shield className="h-5 w-5 text-amber-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Confirm Download</h3>
            </div>
            <p className="text-sm text-gray-600 mb-2">
              You are about to download:
            </p>
            <p className="text-sm font-medium text-gray-900 mb-4 bg-gray-50 p-3 rounded-lg truncate">
              {evidence.fileName}
            </p>
            <p className="text-xs text-gray-500 mb-6">
              This action will be logged in the chain of custody record as a download event.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDownloadConfirm(false)}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDownload}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <Download className="h-4 w-4" />
                Download & Log
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Evidence Viewer Modal */}
      <EvidenceViewerModal
        isOpen={showViewer}
        onClose={() => setShowViewer(false)}
        evidenceId={evidence.id}
        fileName={evidence.fileName}
        fileType={evidence.fileType}
        externalUrl={(evidence as any).externalUrl}
      />
    </>
  );
}

function EvidenceDetailCard({ evidence }: { evidence: EvidenceType }) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState<{
    verified: boolean;
    message: string;
    storedHash?: string;
  } | null>(null);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const { url, filename } = await apiClient.downloadEvidence(evidence.id);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      setDownloadSuccess(true);
      setTimeout(() => setDownloadSuccess(false), 3000);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleVerifyIntegrity = async () => {
    setIsVerifying(true);
    setVerificationResult(null);
    try {
      const result = await apiClient.verifyIntegrity(evidence.id);
      setVerificationResult({
        verified: result.verified,
        message: result.message,
        storedHash: result.storedHash,
      });
    } catch (err) {
      setVerificationResult({
        verified: false,
        message: 'Verification failed - please try again',
      });
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-gray-100 rounded-lg">
            <FileBox className="h-8 w-8 text-gray-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">{evidence.fileName}</h1>
            <p className="text-gray-500">{evidence.evidenceType}</p>
          </div>
        </div>
        <span
          className={`px-3 py-1 rounded-full text-sm font-medium ${
            evidence.integrityStatus === 'verified'
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          {evidence.integrityStatus}
        </span>
      </div>

      <p className="mt-4 text-gray-600">{evidence.description}</p>

      <dl className="mt-6 grid grid-cols-2 gap-4">
        <div>
          <dt className="text-sm text-gray-500">Case Number</dt>
          <dd className="mt-1 font-medium">
            <Link to={`/cases/${evidence.caseNumber}`} className="text-primary-600 hover:text-primary-700">
              {evidence.caseNumber}
            </Link>
          </dd>
        </div>
        <div>
          <dt className="text-sm text-gray-500">File Size</dt>
          <dd className="mt-1 font-medium">{formatFileSize(evidence.fileSize)}</dd>
        </div>
        <div>
          <dt className="text-sm text-gray-500">Uploaded</dt>
          <dd className="mt-1 font-medium">
            {format(new Date(evidence.uploadedAt), 'MMM d, yyyy h:mm a')}
          </dd>
        </div>
        <div>
          <dt className="text-sm text-gray-500">Uploaded By</dt>
          <dd className="mt-1 font-medium">{evidence.uploadedBy}</dd>
        </div>
        <div className="col-span-2">
          <dt className="text-sm text-gray-500 mb-2">Integrity Hash</dt>
          <dd className="flex items-center gap-3">
            <HashDisplay 
              hash={evidence.sha256Hash}
              verified={evidence.integrityStatus === 'verified'}
              showFull
              size="md"
            />
            <button
              onClick={handleVerifyIntegrity}
              disabled={isVerifying}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-600 border border-primary-600 rounded-lg hover:bg-primary-50 disabled:opacity-50"
            >
              {isVerifying ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  <Shield className="h-4 w-4" />
                  Verify Now
                </>
              )}
            </button>
          </dd>
        </div>
      </dl>

      {/* Verification Result Panel */}
      {verificationResult && (
        <div className={`mt-4 p-4 rounded-lg ${
          verificationResult.verified 
            ? 'bg-green-50 border border-green-200' 
            : 'bg-red-50 border border-red-200'
        }`}>
          <div className="flex items-center gap-2">
            {verificationResult.verified ? (
              <CheckCircle className="h-5 w-5 text-green-600" />
            ) : (
              <AlertCircle className="h-5 w-5 text-red-600" />
            )}
            <span className={`font-medium ${
              verificationResult.verified ? 'text-green-800' : 'text-red-800'
            }`}>
              {verificationResult.verified ? 'Integrity Verified' : 'Integrity Check Failed'}
            </span>
          </div>
          <p className={`mt-2 text-sm ${
            verificationResult.verified ? 'text-green-700' : 'text-red-700'
          }`}>
            {verificationResult.message}
          </p>
        </div>
      )}

      <div className="mt-6 flex gap-4">
        <Link
          to={`/custody/${evidence.id}`}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <History className="h-5 w-5" />
          View Custody Chain
        </Link>
        <button
          onClick={handleDownload}
          disabled={isDownloading}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          {isDownloading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : downloadSuccess ? (
            <>
              <CheckCircle className="h-5 w-5" />
              Download Logged
            </>
          ) : (
            <>
              <Download className="h-5 w-5" />
              Download
            </>
          )}
        </button>
      </div>

      {/* Download notice */}
      {downloadSuccess && (
        <div className="mt-4 p-3 bg-blue-50 rounded-lg flex items-center gap-2 text-sm text-blue-800">
          <Shield className="h-4 w-4" />
          This download has been logged in the chain of custody.
        </div>
      )}
    </div>
  );
}
